import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { MouseEvent, PointerEvent as ReactPointerEvent, ReactElement } from 'react';
import ReactSelect from 'react-select';
import type { MultiValue, SingleValue, StylesConfig } from 'react-select';
import styled from 'styled-components';
import {
    Box,
    IconButton,
    Paper,
    Stack,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
} from '@mui/material';
import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import { DarkGreen } from '../../../app/theme/tokens/colors';
import { PrimaryButton, SecondaryButton } from '../../../shared/ui/Inputs';
import { Spinner } from '../../../shared/ui/Spinners';
import { SimplePopup } from '../../../shared/overlays';
import { delCompany, listCompanies } from '../../../services/configuration/companiesService';
import { invalidateCompaniesCatalog } from '../../../hooks/useCompaniesCatalog';
import { CompaniesForm } from './companiesForm';

type CompanyId = string | number;
type SortOrder = 'asc' | 'desc';
type CompanySortColumn =
    | 'cnpj'
    | 'empresa'
    | 'bp'
    | 'divisao'
    | 'nota_fiscal'
    | 'zaeq'
    | 'zrec'
    | 'zvth'
    | 'zven_descartavel'
    | 'zven_recarga';
type CompanyFilterField = 'cnpj' | 'empresa' | 'bp' | 'divisao' | 'nota_fiscal';

interface CompanyRecord {
    id?: CompanyId;
    cnpj?: string | number;
    empresa?: string;
    bp?: string | number;
    divisao?: string | number;
    nota_fiscal?: string;
    zaeq?: string | number;
    zrec?: string | number;
    zvth?: string | number;
    zven_descartavel?: string | number;
    zven_recarga?: string | number;
    [key: string]: unknown;
}

interface CompanyTableColumn {
    id: 'actions' | CompanySortColumn;
    label: string;
    defaultWidth: number;
    sortable: boolean;
    align?: 'center';
}

interface ResizeRule {
    minWidth: number;
    maxWidth: number;
}

interface SelectOption {
    value: string;
    label: string;
}

const ToolBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border-top: solid 1px #b9b9b9;
  padding: 10px 0;
  flex-shrink: 0;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1;
  min-width: 280px;
`;

const FilterBox = styled.div`
  width: 240px;
  min-width: 180px;

  &.filter-value {
    width: 320px;
  }
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #555;
`;

const selectStyles: StylesConfig<SelectOption, boolean> = {
    control: (base, state) => ({
        ...base,
        minHeight: '36px',
        borderColor: state.isFocused ? '#4285f4' : '#d9d9d9',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(66,133,244,0.15)' : 'none',
        '&:hover': { borderColor: '#4285f4' },
        borderRadius: '4px',
        fontSize: '13px',
    }),
    placeholder: (base) => ({ ...base, color: '#aaa', fontSize: '13px' }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        backgroundColor: state.isSelected ? '#e8f0fe' : state.isFocused ? '#f8f9fa' : 'white',
        color: state.isSelected ? '#1a73e8' : '#333',
    }),
    multiValue: (base) => ({ ...base, backgroundColor: '#eef3ee' }),
    multiValueLabel: (base) => ({ ...base, color: '#2f5d3a', fontSize: '12px' }),
    valueContainer: (base) => ({
        ...base,
        maxHeight: '72px',
        overflowY: 'auto',
        alignContent: 'flex-start',
        paddingTop: '4px',
        paddingBottom: '4px',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const Content = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ResizeHandle = styled.span`
  position: absolute;
  top: 0;
  right: -4px;
  z-index: 2;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;

  &::after {
    position: absolute;
    top: 15%;
    left: 3px;
    width: 2px;
    height: 70%;
    content: '';
    background: #318300;
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const COMPANY_TABLE_COLUMNS: CompanyTableColumn[] = [
    { id: 'actions', label: 'Ações', defaultWidth: 82, sortable: false, align: 'center' },
    { id: 'cnpj', label: 'CNPJ', defaultWidth: 140, sortable: true },
    { id: 'empresa', label: 'Empresa', defaultWidth: 420, sortable: true },
    { id: 'bp', label: 'BP', defaultWidth: 110, sortable: true },
    { id: 'divisao', label: 'Divisão', defaultWidth: 120, sortable: true },
    { id: 'nota_fiscal', label: 'NF', defaultWidth: 140, sortable: true },
    { id: 'zaeq', label: 'Prazo de implantação', defaultWidth: 140, sortable: true },
    { id: 'zrec', label: 'Recolhimento', defaultWidth: 140, sortable: true },
    { id: 'zvth', label: 'Assistência Técnica', defaultWidth: 140, sortable: true },
    { id: 'zven_descartavel', label: 'Prazo de reposição de descartável', defaultWidth: 140, sortable: true },
    { id: 'zven_recarga', label: 'Prazo de Recarga', defaultWidth: 140, sortable: true },
];

const COMPANY_COLUMN_RESIZE_RULES: Record<number, ResizeRule> = {
    1: { minWidth: 120, maxWidth: 260 },
    2: { minWidth: 260, maxWidth: 640 },
    3: { minWidth: 90, maxWidth: 200 },
    4: { minWidth: 90, maxWidth: 180 },
    5: { minWidth: 110, maxWidth: 260 },
    6: { minWidth: 130, maxWidth: 520 },
    7: { minWidth: 130, maxWidth: 520 },
    8: { minWidth: 130, maxWidth: 520 },
    9: { minWidth: 130, maxWidth: 520 },
    10: { minWidth: 130, maxWidth: 520 },
};

const COMPANY_COLUMN_DEFINITIONS: Array<{ label: string; getValue: (company: CompanyRecord) => unknown }> = [
    { label: 'Ações', getValue: () => '' },
    { label: 'CNPJ', getValue: (company) => company.cnpj },
    { label: 'Empresa', getValue: (company) => company.empresa },
    { label: 'BP', getValue: (company) => company.bp },
    { label: 'Divisão', getValue: (company) => company.divisao ?? '40' },
    { label: 'NF', getValue: (company) => company.nota_fiscal },
    { label: 'Prazo de implantação', getValue: (company) => company.zaeq },
    { label: 'Recolhimento', getValue: (company) => company.zrec },
    { label: 'Assistência Técnica', getValue: (company) => company.zvth },
    { label: 'Prazo de reposição de descartável', getValue: (company) => company.zven_descartavel },
    { label: 'Prazo de Recarga', getValue: (company) => company.zven_recarga },
];

const COMPANY_COLUMN_WIDTHS = COMPANY_TABLE_COLUMNS.map((column) => column.defaultWidth);

const COMPANY_FILTER_FIELDS: SelectOption[] = [
    { value: 'cnpj', label: 'CNPJ' },
    { value: 'empresa', label: 'Empresa' },
    { value: 'bp', label: 'BP' },
    { value: 'divisao', label: 'Divisão' },
    { value: 'nota_fiscal', label: 'Nota fiscal' },
];

const CollapsedCell = memo(({ value }: { value: unknown }) => {
    const text = value === null || value === undefined || value === '' ? '—' : String(value);

    return (
        <Box
            title={text === '—' ? '' : text}
            sx={{
                display: 'block',
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
            }}
        >
            {text}
        </Box>
    );
});

CollapsedCell.displayName = 'CollapsedCell';

const normalizeText = (value: unknown): string =>
    String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

const getCompanySortValue = (company: CompanyRecord, column: CompanySortColumn): string => {
    const value = column === 'divisao' ? company.divisao ?? '40' : company[column];
    return String(value ?? '');
};

const getCompanyFilterValue = (company: CompanyRecord, field: CompanyFilterField): string => {
    if (field === 'divisao') return String(company.divisao ?? '40');
    return String(company[field] ?? '');
};

const makeUniqueOptions = (values: SelectOption[]): SelectOption[] => {
    const options = new Map<string, SelectOption>();
    values.forEach((option) => {
        if (!option.value || !option.label || options.has(option.value)) return;
        options.set(option.value, option);
    });
    return Array.from(options.values()).sort((first, second) =>
        first.label.localeCompare(second.label, 'pt-BR', { sensitivity: 'base', numeric: true })
    );
};

const sortCompanies = (list: CompanyRecord[], order: SortOrder, orderBy: CompanySortColumn): CompanyRecord[] =>
    [...list].sort((firstCompany, secondCompany) => {
        const first = normalizeText(getCompanySortValue(firstCompany, orderBy));
        const second = normalizeText(getCompanySortValue(secondCompany, orderBy));
        if (!first && !second) return 0;
        if (!first) return 1;
        if (!second) return -1;
        const comparison = first.localeCompare(second, 'pt-BR', { sensitivity: 'base', numeric: true });
        return order === 'asc' ? comparison : -comparison;
    });

const getCompanyList = (payload: unknown): CompanyRecord[] => {
    if (Array.isArray(payload)) return payload as CompanyRecord[];
    if (!payload || typeof payload !== 'object') return [];
    const response = payload as Record<string, unknown>;
    if (Array.isArray(response.data)) return response.data as CompanyRecord[];
    if (Array.isArray(response.body)) return response.body as CompanyRecord[];
    return [];
};

export const CompaniesConfig = (): ReactElement => {
    const [companies, setCompanies] = useState<CompanyRecord[]>([]);
    const [currentCompany, setCurrentCompany] = useState<Record<string, unknown>>({});
    const [shouldShowEditPopup, setShouldShowEditPopup] = useState(false);
    const [shouldShowDeletePopup, setShouldShowDeletePopup] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState<CompanyRecord | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [columnWidths, setColumnWidths] = useState<number[]>(() => [...COMPANY_COLUMN_WIDTHS]);
    const [order, setOrder] = useState<SortOrder>('asc');
    const [orderBy, setOrderBy] = useState<CompanySortColumn>('empresa');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedFilterField, setSelectedFilterField] = useState<SelectOption | null>(null);
    const [selectedFilterValues, setSelectedFilterValues] = useState<SelectOption[]>([]);

    const fetchCompanies = async (): Promise<void> => {
        setIsLoading(true);
        try {
            const response = await listCompanies();
            setCompanies(getCompanyList(response));
        } catch (error: unknown) {
            console.warn('Erro ao buscar empresas da API:', error);
            setCompanies([]);
        } finally {
            setCurrentCompany({});
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const showDeletePopup = useCallback((company: CompanyRecord): void => {
        setCompanyToDelete(company);
        setDeleteError('');
        setShouldShowDeletePopup(true);
    }, []);

    const showEditPopup = useCallback((company: CompanyRecord): void => {
        setCurrentCompany(company);
        setIsEditing(true);
        setShouldShowEditPopup(true);
    }, []);

    const onAction = (): void => {
        setShouldShowEditPopup(false);
        setIsEditing(false);
        setCurrentCompany({});
        fetchCompanies();
    };

    const updateColumnWidth = useCallback((columnIndex: number, width: number): void => {
        setColumnWidths((current) => {
            if (current[columnIndex] === width) return current;
            const next = [...current];
            next[columnIndex] = width;
            return next;
        });
    }, []);

    const getAutoColumnWidth = useCallback((columnIndex: number): number | null => {
        const column = COMPANY_COLUMN_DEFINITIONS[columnIndex];
        if (!column || !COMPANY_COLUMN_RESIZE_RULES[columnIndex]) return null;

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) context.font = '12px Arial';

        const measure = (value: unknown): number => {
            const text = value === null || value === undefined || value === '' ? '—' : String(value);
            return context ? context.measureText(text).width : text.length * 7.2;
        };

        const contentWidth = (Array.isArray(companies) ? companies : []).reduce(
            (largest, company) => Math.max(largest, measure(column.getValue(company))),
            measure(column.label)
        );

        // Espaço para padding da célula e para o manipulador de redimensionamento.
        return contentWidth + 24;
    }, [companies]);

    const startColumnResize = (event: ReactPointerEvent<HTMLSpanElement>, columnIndex: number): void => {
        const rules = COMPANY_COLUMN_RESIZE_RULES[columnIndex];
        if (!rules || event.button !== 0) return;

        event.preventDefault();
        event.stopPropagation();

        const startWidth = columnWidths[columnIndex];
        const startX = event.clientX;
        let pendingWidth = startWidth;

        const clampWidth = (width: number): number =>
            Math.min(rules.maxWidth, Math.max(rules.minWidth, Math.round(width)));

        const handleMove = (moveEvent: globalThis.PointerEvent): void => {
            pendingWidth = clampWidth(startWidth + moveEvent.clientX - startX);
            updateColumnWidth(columnIndex, pendingWidth);
        };

        const handleEnd = (): void => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleEnd);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleEnd, { once: true });
    };

    const autoSizeColumn = (event: MouseEvent<HTMLSpanElement>, columnIndex: number): void => {
        event.preventDefault();
        event.stopPropagation();
        const measuredWidth = getAutoColumnWidth(columnIndex);
        const rules = COMPANY_COLUMN_RESIZE_RULES[columnIndex];
        if (!measuredWidth || !rules) return;
        updateColumnWidth(
            columnIndex,
            Math.min(rules.maxWidth, Math.max(rules.minWidth, Math.round(measuredWidth)))
        );
    };

    const selectedFilterOptions = useMemo<SelectOption[]>(() => {
        const field = selectedFilterField?.value as CompanyFilterField | undefined;
        if (!field) return [];

        return makeUniqueOptions(
            companies.map((company) => {
                const value = getCompanyFilterValue(company, field);
                return { value: normalizeText(value), label: value };
            })
        );
    }, [companies, selectedFilterField]);

    const selectedFilterPlaceholder = selectedFilterField
        ? `Filtrar ${selectedFilterField.label.toLowerCase()}...`
        : 'Selecione um filtro primeiro';

    const filteredCompanies = useMemo(() => {
        const field = selectedFilterField?.value as CompanyFilterField | undefined;
        const selectedValues = new Set(selectedFilterValues.map((option) => option.value));
        if (!field || selectedValues.size === 0) return companies;

        return companies.filter((company) =>
            selectedValues.has(normalizeText(getCompanyFilterValue(company, field)))
        );
    }, [companies, selectedFilterField, selectedFilterValues]);

    const sortedCompanies = useMemo(
        () => sortCompanies(filteredCompanies, order, orderBy),
        [filteredCompanies, order, orderBy]
    );
    const lastPage = Math.max(0, Math.ceil(sortedCompanies.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const visibleCompanies = sortedCompanies.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
    );

    const requestSort = (property: CompanySortColumn): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const deleteCompanyAction = async () => {
        if (!companyToDelete?.id) return;

        setIsDeleting(true);
        setDeleteError('');

        try {
            const resp = await delCompany(companyToDelete.id);
            if (resp?.httpStatus && resp.httpStatus >= 400) {
                const msg =
                    resp?.body?.message ||
                    resp?.body?.error ||
                    (typeof resp?.body === 'string' ? resp.body : null);
                throw new Error(
                    msg || `Erro ao excluir empresa (HTTP ${resp.httpStatus})`
                );
            }

            setShouldShowDeletePopup(false);
            setCompanyToDelete(null);
            invalidateCompaniesCatalog();

            await fetchCompanies();
        } catch (error: unknown) {
            console.warn('Erro ao deletar empresa:', error);
            setDeleteError(error instanceof Error ? error.message : 'Não foi possível excluir a empresa');
        } finally {
            setIsDeleting(false);
        }
    };

    const EditCompanyPopup = () => (
        <SimplePopup
            width={1100}
            height={820}
            zIndex={1000}
            outlet={
                <div
                    style={{
                        padding: '10px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '20px',
                            paddingBottom: '15px',
                            borderBottom: '2px solid #e0e0e0',
                            flexShrink: 0,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <h2
                                style={{
                                    fontSize: '24px',
                                    color: '#333',
                                    fontWeight: 600,
                                    margin: 0,
                                    letterSpacing: '-0.5px',
                                }}
                            >
                                {isEditing ? 'Editar Empresa' : 'Nova Empresa'}
                            </h2>
                            <p
                                style={{
                                    fontSize: '14px',
                                    color: '#666',
                                    margin: '5px 0 0 0',
                                }}
                            >
                                {isEditing
                                    ? 'Atualize as informações da empresa'
                                    : 'Preencha os dados para adicionar uma nova empresa (CNPJ)'}
                            </p>
                        </div>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <CompaniesForm
                            id={currentCompany.id ?? ''}
                            cnpj={currentCompany.cnpj ?? ''}
                            empresa={currentCompany.empresa ?? ''}
                            bp={currentCompany.bp ?? ''}
                            divisao={currentCompany.divisao ?? '40'}
                            nota_fiscal={currentCompany.nota_fiscal ?? ''}
                            zaeq={currentCompany.zaeq ?? ''}
                            zrec={currentCompany.zrec ?? ''}
                            zvth={currentCompany.zvth ?? ''}
                            zven_descartavel={currentCompany.zven_descartavel ?? ''}
                            zven_recarga={currentCompany.zven_recarga ?? ''}
                            isEditing={isEditing}
                            onAction={onAction}
                        />
                    </div>
                </div>
            }
        />
    );

    const DeleteCompanyPopup = () => (
        <SimplePopup
            width={400}
            height={250}
            zIndex={1000}
            outlet={
                <div style={{ display: 'block' }}>
                    <p style={{ textAlign: 'center', fontWeight: '500' }}>
                        Tem certeza que deseja excluir a empresa &quot;
                        {companyToDelete?.empresa}
                        &quot; (CNPJ: {companyToDelete?.cnpj})?
                    </p>

                    {deleteError ? (
                        <p
                            style={{
                                color: '#b00020',
                                marginTop: 10,
                                marginBottom: 10,
                                fontSize: 13,
                                textAlign: 'center',
                            }}
                        >
                            {deleteError}
                        </p>
                    ) : null}

                    {isDeleting ? (
                        <div
                            style={{ width: '100%', textAlign: 'center', padding: '10px 0' }}
                        >
                            <Spinner />
                        </div>
                    ) : null}

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <SecondaryButton
                            style={{ width: '100px' }}
                            disabled={isDeleting}
                            onClick={() => setShouldShowDeletePopup(false)}
                        >
                            Não
                        </SecondaryButton>

                        <PrimaryButton
                            style={{ width: '100px' }}
                            disabled={isDeleting}
                            onClick={deleteCompanyAction}
                        >
                            {isDeleting ? 'Excluindo...' : 'Sim'}
                        </PrimaryButton>
                    </div>
                </div>
            }
        />
    );

    return (
        <Content>
            {shouldShowEditPopup ? EditCompanyPopup() : <></>}
            {shouldShowDeletePopup ? DeleteCompanyPopup() : <></>}
            <ToolBar>
                <FilterBar>
                    <FilterBox>
                        <FilterLabel>Filtro</FilterLabel>
                        <ReactSelect<SelectOption, false>
                            options={COMPANY_FILTER_FIELDS}
                            value={selectedFilterField}
                            onChange={(value: SingleValue<SelectOption>) => {
                                setSelectedFilterField(value);
                                setSelectedFilterValues([]);
                                setPage(0);
                            }}
                            placeholder="Escolha o filtro..."
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            isClearable
                            noOptionsMessage={() => 'Nenhum filtro encontrado'}
                        />
                    </FilterBox>
                    <FilterBox className="filter-value">
                        <FilterLabel>Valor</FilterLabel>
                        <ReactSelect<SelectOption, true>
                            isMulti
                            isDisabled={!selectedFilterField}
                            options={selectedFilterOptions}
                            value={selectedFilterValues}
                            onChange={(value: MultiValue<SelectOption>) => {
                                setSelectedFilterValues(Array.from(value));
                                setPage(0);
                            }}
                            placeholder={selectedFilterPlaceholder}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            noOptionsMessage={() => 'Nenhum valor encontrado'}
                        />
                    </FilterBox>
                </FilterBar>
                <div style={{ display: 'flex', marginLeft: 'auto' }}>
                    <PrimaryButton
                        disabled={isLoading}
                        style={{
                            opacity: isLoading ? 0.7 : 1,
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                        }}
                        onClick={() => {
                            setIsEditing(false);
                            setCurrentCompany({});
                            setShouldShowEditPopup(true);
                        }}
                    >
                        Nova empresa
                    </PrimaryButton>
                </div>
            </ToolBar>
            <TableWrapper>
                {isLoading ? (
                    <Box sx={{ width: '100%', textAlign: 'center', py: 2.5 }}>
                        <Spinner />
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflow: 'auto',
                                border: '1px solid #e6e6e6',
                                borderRadius: 1,
                            }}
                        >
                            <MuiTable
                                stickyHeader
                                size="small"
                                aria-label="Gerenciamento de empresas"
                                sx={{
                                    tableLayout: 'fixed',
                                    minWidth: columnWidths.reduce((total, width) => total + width, 0),
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        {COMPANY_TABLE_COLUMNS.map((column, columnIndex) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.align}
                                                sx={{
                                                    position: 'relative',
                                                    width: columnWidths[columnIndex],
                                                    minWidth: columnWidths[columnIndex],
                                                    backgroundColor: DarkGreen,
                                                    color: 'white',
                                                    fontWeight: 700,
                                                    whiteSpace: 'normal',
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                {column.sortable ? (
                                                    <TableSortLabel
                                                        active={orderBy === column.id}
                                                        direction={orderBy === column.id ? order : 'asc'}
                                                        onClick={() => requestSort(column.id as CompanySortColumn)}
                                                        sx={{
                                                            color: 'white !important',
                                                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                                                        }}
                                                    >
                                                        {column.label}
                                                    </TableSortLabel>
                                                ) : column.label}
                                                {COMPANY_COLUMN_RESIZE_RULES[columnIndex] && (
                                                    <ResizeHandle
                                                        role="separator"
                                                        aria-label={`Redimensionar coluna ${column.label}`}
                                                        onPointerDown={(event) => startColumnResize(event, columnIndex)}
                                                        onDoubleClick={(event) => autoSizeColumn(event, columnIndex)}
                                                    />
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleCompanies.map((company) => (
                                        <TableRow hover key={company.id ?? `${company.cnpj}-${company.empresa}`}>
                                            <TableCell align="center">
                                                <Stack direction="row" justifyContent="center" spacing={0.25}>
                                                    <Tooltip title="Excluir empresa">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Excluir empresa ${company.empresa ?? company.cnpj ?? ''}`}
                                                            onClick={() => showDeletePopup(company)}
                                                        >
                                                            <DeleteOutline fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Editar empresa">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Editar empresa ${company.empresa ?? company.cnpj ?? ''}`}
                                                            onClick={() => showEditPopup(company)}
                                                        >
                                                            <EditOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                            <TableCell><CollapsedCell value={company.cnpj} /></TableCell>
                                            <TableCell><CollapsedCell value={company.empresa} /></TableCell>
                                            <TableCell><CollapsedCell value={company.bp} /></TableCell>
                                            <TableCell><CollapsedCell value={company.divisao ?? '40'} /></TableCell>
                                            <TableCell><CollapsedCell value={company.nota_fiscal} /></TableCell>
                                            <TableCell><CollapsedCell value={company.zaeq} /></TableCell>
                                            <TableCell><CollapsedCell value={company.zrec} /></TableCell>
                                            <TableCell><CollapsedCell value={company.zvth} /></TableCell>
                                            <TableCell><CollapsedCell value={company.zven_descartavel} /></TableCell>
                                            <TableCell><CollapsedCell value={company.zven_recarga} /></TableCell>
                                        </TableRow>
                                    ))}
                                    {visibleCompanies.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={COMPANY_TABLE_COLUMNS.length} align="center" sx={{ py: 5 }}>
                                                Nenhuma empresa encontrada.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </MuiTable>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={sortedCompanies.length}
                            page={currentPage}
                            onPageChange={(_, nextPage) => setPage(nextPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(parseInt(event.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            labelRowsPerPage="Linhas por página"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                            sx={{ flexShrink: 0, overflow: 'hidden' }}
                        />
                    </>
                )}
            </TableWrapper>
        </Content>
    );
};
